import { useEffect, useState } from "react";
import { normalizeAnalyticsWindow } from "../../core/analyticsWindows";
import { fetchCreditsInsights } from "./api";

export default function useCreditsInsights(initialWindow = "30d", { activeSubscription = false } = {}) {
  const [windowKey, setWindowKey] = useState(initialWindow);
  const [activityPage, setActivityPage] = useState(1);
  const [state, setState] = useState({
    loading: true,
    loadingMore: false,
    error: "",
    data: null,
  });

  useEffect(() => {
    setWindowKey((current) => normalizeAnalyticsWindow(current, activeSubscription));
  }, [activeSubscription]);

  useEffect(() => {
    let cancelled = false;
    const normalizedWindow = normalizeAnalyticsWindow(windowKey, activeSubscription);

    async function load() {
      const isLoadMore = activityPage > 1;
      setState((prev) => ({
        ...prev,
        loading: !isLoadMore,
        loadingMore: isLoadMore,
        error: "",
      }));
      try {
        const data = await fetchCreditsInsights(normalizedWindow, { activityPage });
        if (cancelled) return;
        setState((prev) => {
          if (!isLoadMore || !prev.data) {
            return { loading: false, loadingMore: false, error: "", data };
          }
          return {
            loading: false,
            loadingMore: false,
            error: "",
            data: {
              ...data,
              recent_activity: [
                ...(prev.data.recent_activity || []),
                ...(data.recent_activity || []),
              ],
            },
          };
        });
      } catch (error) {
        if (cancelled) return;
        setState((prev) => ({
          loading: false,
          loadingMore: false,
          error: error?.message || "credits_insights_load_failed",
          data: isLoadMore ? prev.data : null,
        }));
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [windowKey, activityPage, activeSubscription]);

  const loadMoreActivity = () => {
    if (state.loading || state.loadingMore) return;
    if (!state.data?.recent_activity_pagination?.has_more) return;
    setActivityPage((prev) => prev + 1);
  };

  const updateWindow = (nextWindow) => {
    setWindowKey(normalizeAnalyticsWindow(nextWindow, activeSubscription));
    setActivityPage(1);
  };

  return {
    ...state,
    windowKey: normalizeAnalyticsWindow(windowKey, activeSubscription),
    setWindowKey: updateWindow,
    loadMoreActivity,
    hasData: Boolean(state.data),
  };
}
